import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper,
  Card,
  CardContent,
  CardActions,
  Divider,
  createTheme,
  ThemeProvider,
  Switch,
  FormControlLabel,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import {
  Add as AddIcon,
  Email as EmailIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import axios from "axios";
import useUserId from "../../hooks/useUserId";
import TemplateForm from "./TemplateForm";
import DragAndDropEmailEditor from "./DragAndDropEmailEditor";
import SendEmailModal from "./SendEmailModal";
import { useTranslation } from "react-i18next";
import CustomPagination from "views/client/pagination";

// Define modern theme matching caravan section
const theme = createTheme({
  palette: {
    primary: {
      main: "#0017E7", // Electric blue
      light: "#4d5aff",
      dark: "#0010a3",
      contrastText: "#fff",
    },
    secondary: {
      main: "#1e40af", // Complementary blue
      light: "#3b82f6",
      dark: "#1e3a8a",
      contrastText: "#fff",
    },
    background: {
      default: "#f8fafc", // Very light blue-gray
      paper: "#ffffff",
    },
    text: {
      primary: "#1e293b", // Dark slate
      secondary: "#475569", // Medium slate
    },
    divider: "#e2e8f0", // Light slate
    info: {
      main: "#0017E7", // Electric blue for info
    },
    success: {
      main: "#059669", // Emerald green
    },
    warning: {
      main: "#d97706", // Amber
    },
    error: {
      main: "#dc2626", // Red
    },
  },
  shape: {
    borderRadius: 10, // Rounded corners
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 600,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          boxShadow: "none",
          fontWeight: 500,
          borderRadius: 8,
        },
        containedPrimary: {
          background: "linear-gradient(45deg, #0017E7 30%, #1e40af 90%)",
          "&:hover": {
            background: "linear-gradient(45deg, #0010a3 30%, #1e3a8a 90%)",
            boxShadow: "0px 4px 15px rgba(0, 23, 231, 0.4)",
          },
        },
        outlinedPrimary: {
          borderColor: "#0017E7",
          color: "#0017E7",
          "&:hover": {
            borderColor: "#0010a3",
            backgroundColor: "rgba(0, 23, 231, 0.04)",
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: "0px 3px 15px rgba(0, 0, 0, 0.05)",
          transition: "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
          "&:hover": {
            transform: "translateY(-3px)",
            boxShadow: "0px 8px 25px rgba(0, 0, 0, 0.1)",
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 500,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 12,
        },
      },
    },
  },
});

const EmailTemplates = () => {
  const { t } = useTranslation();
  const { userId } = useUserId();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isDragDropEditorOpen, setIsDragDropEditorOpen] = useState(false);
  const [currentTemplate, setCurrentTemplate] = useState(null);
  const [isSendModalOpen, setIsSendModalOpen] = useState(false);
  const [templateToSend, setTemplateToSend] = useState(null);
  const [page, setPage] = useState(0); // CustomPagination uses 0-based indexing
  const [rowsPerPage, setRowsPerPage] = useState(6); // 6 templates per page
  const [allTemplates, setAllTemplates] = useState([]); // Store all templates for frontend pagination
  const [useAdvancedEditor, setUseAdvancedEditor] = useState(true); // Default to advanced editor
  const apiUrl = process.env.REACT_APP_API_URL;
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${apiUrl}recruiters/${userId}/templates/`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
          params: {
            page_size: 1000, // Request all templates for frontend pagination
          },
        }
      );

      // Handle different response formats
      let templatesData = [];
      if (response.data.results) {
        // Paginated response
        templatesData = response.data.results;
        console.log(
          `Loaded ${
            templatesData.length
          } templates from paginated response. Total: ${
            response.data.count || "unknown"
          }`
        );
      } else if (Array.isArray(response.data)) {
        // Direct array response
        templatesData = response.data;
        console.log(
          `Loaded ${templatesData.length} templates from direct array response`
        );
      } else {
        console.warn("Unexpected data format:", response.data);
        templatesData = [];
      }

      setAllTemplates(templatesData);
      setError(null);
    } catch (err) {
      setError(t("emailTemplates.failedToLoadTemplates"));
      console.error("Error fetching templates:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userId) {
      fetchTemplates();
    }
  }, [userId]);

  // Update templates when pagination changes
  useEffect(() => {
    const startIndex = page * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    const paginatedTemplates = allTemplates.slice(startIndex, endIndex);
    setTemplates(paginatedTemplates);
  }, [allTemplates, page, rowsPerPage]);

  const handleChangePage = (_, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleCreateNew = () => {
    setCurrentTemplate(null);
    if (useAdvancedEditor) {
      setIsDragDropEditorOpen(true);
    } else {
      setIsFormOpen(true);
    }
  };

  // const handleEdit = (template) => {
  //   setCurrentTemplate(template);
  //   // If the template has a design property, use the drag-drop editor
  //   // Otherwise use the standard form (for backward compatibility)
  //   if (template.design || useAdvancedEditor) {
  //     setIsDragDropEditorOpen(true);
  //   } else {
  //     setIsFormOpen(true);
  //   }
  // };
  const handleEdit = (template) => {
    setCurrentTemplate(template);
    // Only check if the template has a design property to determine editor type
    if (template.design) {
      setIsDragDropEditorOpen(true);
      setUseAdvancedEditor(true); // Auto-switch the toggle to advanced
    } else {
      setIsFormOpen(true);
      setUseAdvancedEditor(false); // Auto-switch the toggle to normal
    }
  };

  const handleDelete = (template) => {
    setTemplateToDelete(template);
    setDeleteConfirmOpen(true);
  };
  const confirmDelete = async () => {
    try {
      await axios.delete(
        `${apiUrl}recruiters/${userId}/templates/${templateToDelete.id}/`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        }
      );
      // Refresh templates after deletion
      fetchTemplates();
      setDeleteConfirmOpen(false);
      setTemplateToDelete(null);
    } catch (err) {
      setError(t("emailTemplates.failedToDeleteTemplate"));
      console.error("Error deleting template:", err);
      setDeleteConfirmOpen(false);
    }
  };
  const cancelDelete = () => {
    setDeleteConfirmOpen(false);
    setTemplateToDelete(null);
  };

  const handleFormClose = () => {
    setIsFormOpen(false);
    setIsDragDropEditorOpen(false);
    setCurrentTemplate(null);
  };

  const handleFormSubmit = async (templateData) => {
    try {
      if (currentTemplate) {
        // Update existing template
        await axios.put(
          `${apiUrl}recruiters/${userId}/templates/${currentTemplate.id}/`,
          templateData,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
            },
          }
        );
      } else {
        // Create new template
        await axios.post(
          `${apiUrl}recruiters/${userId}/templates/`,
          templateData,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
            },
          }
        );
      }

      setIsFormOpen(false);
      setIsDragDropEditorOpen(false);
      fetchTemplates();
    } catch (err) {
      setError(t("emailTemplates.failedToSaveTemplate"));
      console.error("Error saving template:", err);
    }
  };

  const handleSendEmail = (template) => {
    setTemplateToSend(template);
    setIsSendModalOpen(true);
  };

  const handleSendModalClose = () => {
    setIsSendModalOpen(false);
    setTemplateToSend(null);
  };

  const toggleEditorType = (event) => {
    setUseAdvancedEditor(event.target.checked);
  };

  if (!userId) {
    return (
      <ThemeProvider theme={theme}>
        <Container maxWidth="lg" sx={{ mt: 4 }}>
          <Typography>{t("emailTemplates.userAuthRequired")}</Typography>
        </Container>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteConfirmOpen}
          onClose={cancelDelete}
          aria-labelledby="delete-dialog-title"
          aria-describedby="delete-dialog-description"
        >
          <DialogTitle id="delete-dialog-title">
            {t("emailTemplates.deleteTemplateTitle")}
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="delete-dialog-description">
              {t("emailTemplates.deleteConfirmation")}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={cancelDelete} color="primary">
              {t("emailTemplates.cancelDelete")}
            </Button>
            <Button onClick={confirmDelete} color="error" autoFocus>
              {t("emailTemplates.confirmDelete")}
            </Button>
          </DialogActions>
        </Dialog>

        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={4}
        >
          <Typography variant="h4" component="h1" color="primary">
            {t("emailTemplates.emailTemplates")}
          </Typography>
          <Box display="flex" alignItems="center" gap={2}>
            <FormControlLabel
              control={
                <Switch
                  checked={useAdvancedEditor}
                  onChange={toggleEditorType}
                  color="primary"
                />
              }
              label={
                useAdvancedEditor
                  ? t("emailTemplates.advancedEditor")
                  : t("emailTemplates.simpleEditor")
              }
            />
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleCreateNew}
            >
              {t("emailTemplates.createNewTemplate")}
            </Button>
          </Box>
        </Box>
        {error && (
          <Paper
            elevation={0}
            sx={{
              p: 2,
              mb: 3,
              backgroundColor: theme.palette.error.light,
              color: theme.palette.error.contrastText,
            }}
          >
            <Typography>{error}</Typography>
          </Paper>
        )}
        {loading ? (
          <Box display="flex" justifyContent="center" mt={4}>
            <CircularProgress />
          </Box>
        ) : templates.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: "center", backgroundColor: "#f8f9fa" }}>
            <Typography variant="h6" gutterBottom>
              {t("emailTemplates.noEmailTemplatesYet")}
            </Typography>
            <Typography color="textSecondary" mb={2}>
              {t("emailTemplates.createFirstTemplate")}
            </Typography>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleCreateNew}
            >
              {t("emailTemplates.createTemplate")}
            </Button>
          </Paper>
        ) : (
          <>
            <Grid container spacing={3}>
              {templates.map((template) => (
                <Grid item xs={12} sm={6} md={4} key={template.id}>
                  <Card
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      transition: "all 0.2s",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: 3,
                      },
                    }}
                  >
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                      >
                        <Typography
                          variant="h6"
                          component="h2"
                          gutterBottom
                          noWrap
                          sx={{ flex: 1 }}
                        >
                          {template.name}
                        </Typography>
                        {template.design && (
                          <Chip
                            label={t("emailTemplates.advanced")}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        )}
                      </Box>
                      <Typography color="textSecondary" gutterBottom noWrap>
                        {t("emailTemplates.subjectLabel")}: {template.subject}
                      </Typography>
                      <Divider sx={{ my: 1.5 }} />
                      <Box
                        component="div"
                        sx={{
                          height: "160px",
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          overflow: "hidden",
                          position: "relative",
                          border: "1px solid #e0e0e0",
                          borderRadius: "8px",
                          backgroundColor: "#f8f9fa",
                          padding: "8px",
                          "& > div": template.design
                            ? {
                                // Advanced template styling
                                transform: "translateX(-25%) scale(0.5)",
                                transformOrigin: "center center",
                                width: "100%",
                                height: "auto",
                                overflow: "visible",
                                display: "block",
                                margin: "0 auto",
                                textAlign: "center",
                              }
                            : {
                                // Simple template styling
                                transform: "scale(0.6)",
                                transformOrigin: "center center",
                                width: "100%",
                                height: "auto",
                                overflow: "hidden",
                                display: "block",
                                margin: "0 auto",
                                textAlign: "center",
                                padding: "10px",
                              },
                          // Advanced template specific overrides
                          ...(template.design && {
                            "& .email-container": {
                              maxWidth: "none !important",
                              width: "100% !important",
                              margin: "0 auto !important",
                            },
                            "& .header, & .content, & .footer": {
                              textAlign: "center !important",
                            },
                            "& table": {
                              margin: "0 auto !important",
                              textAlign: "center",
                            },
                          }),
                          // Common styling for all templates
                          "& img": {
                            maxWidth: "100%",
                            height: "auto",
                            display: "block",
                            margin: "0 auto !important",
                          },
                        }}
                      >
                        <div
                          dangerouslySetInnerHTML={{
                            __html: template.body,
                          }}
                        />
                      </Box>
                    </CardContent>
                    <CardActions>
                      <Button
                        size="small"
                        onClick={() => handleEdit(template)}
                        startIcon={<EditIcon />}
                      >
                        {t("emailTemplates.editTemplate")}
                      </Button>
                      <Button
                        size="small"
                        color="error"
                        onClick={() => handleDelete(template)}
                      >
                        {t("emailTemplates.deleteTemplate")}
                      </Button>
                      <Button
                        size="small"
                        color="primary"
                        startIcon={<EmailIcon />}
                        onClick={() => handleSendEmail(template)}
                        sx={{ marginLeft: "auto" }}
                      >
                        {t("emailTemplates.useTemplate")}
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {/* Pagination controls */}
            {allTemplates.length > 0 && (
              <Box display="flex" justifyContent="center" mt={4} mb={2}>
                <CustomPagination
                  page={page}
                  handleChangePage={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  count={allTemplates.length}
                />
              </Box>
            )}
          </>
        )}
        {/* Standard Text-based Email Template Editor */}
        {isFormOpen && (
          <TemplateForm
            open={isFormOpen}
            onClose={handleFormClose}
            onSubmit={handleFormSubmit}
            template={currentTemplate}
            theme={theme}
          />
        )}
        {/* Advanced Drag & Drop Email Template Editor */}
        {isDragDropEditorOpen && (
          <DragAndDropEmailEditor
            open={isDragDropEditorOpen}
            onClose={handleFormClose}
            onSubmit={handleFormSubmit}
            template={currentTemplate}
            theme={theme}
          />
        )}
        {/* Send Email Modal (remains the same) */}
        {isSendModalOpen && templateToSend && (
          <SendEmailModal
            open={isSendModalOpen}
            onClose={handleSendModalClose}
            template={templateToSend}
            recruiterId={userId}
            theme={theme}
            candidateInfo={null}
            candidates={[]}
            showTemplateSelection={false}
          />
        )}
      </Container>
    </ThemeProvider>
  );
};

export default EmailTemplates;
